import { useState, useRef, useEffect } from "react";
import * as React from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const VenueImageUpload = ({ images, onImagesUpdate }) => {
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
        setShowCropDialog(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        16 / 9, // Landscape aspect ratio for venue images
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  };

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Set canvas size to desired output (800x450 for venue images)
    canvas.width = 800;
    canvas.height = 450;
    
    const ctx = canvas.getContext("2d");
    
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      800,
      450
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            toast.error("Resim işlenemedi");
            return;
          }
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            resolve(reader.result);
          };
        },
        "image/jpeg",
        0.8
      );
    });
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      const croppedImage = await getCroppedImg();
      
      if (!croppedImage) {
        if (isMounted) {
          toast.error("Resim kırpılamadı");
          setUploading(false);
        }
        return;
      }

      const newImages = [...images, croppedImage];
      onImagesUpdate(newImages);
      
      if (isMounted) {
        setShowCropDialog(false);
        setImgSrc("");
        toast.success("Resim eklendi");
      }
    } catch (error) {
      console.error("Upload error:", error);
      if (isMounted) {
        toast.error("Resim yüklenemedi");
      }
    } finally {
      if (isMounted) {
        setUploading(false);
      }
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesUpdate(newImages);
    toast.success("Resim kaldırıldı");
  };

  return (
    <>
      <div className="space-y-3">
        {/* Image Preview Grid */}
        {images.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {images.map((img, index) => (
              <Card key={index} className="relative group overflow-hidden">
                <img 
                  src={img} 
                  alt={`Saha ${index + 1}`}
                  className="w-full h-32 object-cover"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                  onClick={() => handleRemoveImage(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Button */}
        <Button
          type="button"
          variant="outline"
          className="w-full border-dashed border-2 h-24"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-6 w-6 text-gray-400" />
            <div className="text-sm">
              <span className="font-semibold">Fotoğraf Yükle</span>
              <p className="text-xs text-gray-500">Galeriden seçin</p>
            </div>
          </div>
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onSelectFile}
          className="hidden"
        />
      </div>

      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Saha Fotoğrafını Düzenle</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={16 / 9}
              >
                <img
                  ref={imgRef}
                  alt="Crop"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  className="max-h-96"
                />
              </ReactCrop>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCropDialog(false);
                setImgSrc("");
              }}
              disabled={uploading}
            >
              İptal
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? "Yükleniyor..." : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VenueImageUpload;
